import { put, call, select } from 'redux-saga/effects';
import { resetLimits } from '../actions/limitsActions';
import { resetUserPlayStatsAndLimits } from '../actions/userPlayStatsAndLimitsActions';
import { resetLoginHistory } from '../actions/loginHistoryActions';
import { autoLoginUpdate, refreshUser } from '../actions/autoLoginActions';
import { AutoLoginStep } from '@/models/enum/auto-login-step.enum';
import { kycReset } from '../actions/kycActions';
import { BUILD_MODE, envIsNormal } from '@/constants/constants';
import { resetConsents } from '../actions/consentsActions';
import { setFitToPlay, setOntarioLocationCheckStatus } from '../actions/commonActions';
import { resetAml } from '../actions/amlActions';
import { GTagEvents, triggerGTag } from '@/lib/gTagManager';
import CookiesManager from '../../shared/cookies-manager';
import { loginSuccessDone, logoutSuccess, setTimeoutBlocked } from '@/store/actions/auth';
import { MODALS } from '@/features/modal/types';
import { modalOpen } from '@/store/modal/actions';
import { clearUser } from '@/store/actions/user';
import BffApiService from '@/shared/bffApiService';
import { useFluidPaymentsStore } from '@/features/cashier/fluid/fluidPaymentsStore';
import { OntarioLocationCheckStatus } from '@/models/enum/ontario-location-check-status.enum';
import { getBonuses, resetBonusesState } from '@/store/bonuses/actions';
import { sendEventToAppWrapper } from '@/utils/app-wrapper-utils';
import { BlockType } from '@lucky7ventures/bff-types';
import { selectIsFin } from '@/store/selectors/commonSelectors';

// Session-level flag to track if login event has been sent
let hasTrackedLogin = false;

// Send login event once per session when user data is available
export function sendLoginEventOnceSaga(action) {
  if (!hasTrackedLogin && action.user?.userId) {
    sendEventToAppWrapper(GTagEvents.login_success, {
      userId: action.user?.userId,
    });
    hasTrackedLogin = true;
  }
}

// Login success
export function* loginSuccessSaga(action) {
  const { token, blockReason, isAutoLogin } = action;

  // Non-timeout blocks: reject login entirely — log the user out
  if (blockReason && blockReason.blockType !== BlockType.TimeoutUserBlock) {
    CookiesManager.removeTokenCookie();
    yield put(modalOpen(MODALS.FAILED_LOGIN));
    yield put(loginSuccessDone());
    return;
  }

  CookiesManager.setTokenCookie(token);

  // For auto-login, use the pre-computed flag from localStorage.
  // For fresh login, derive it from blockReason + env/locale conditions.
  const isFin = yield select(selectIsFin);
  const isTimeoutBlocked = isAutoLogin
    ? action.isTimeoutBlocked
    : blockReason?.blockType === BlockType.TimeoutUserBlock && envIsNormal() && !isFin;

  // Persist or clear timeout flag in localStorage
  if (!isAutoLogin) {
    if (isTimeoutBlocked) {
      localStorage.setItem('isTimeoutBlocked', 'true');
    } else {
      localStorage.removeItem('isTimeoutBlocked');
    }
  }

  yield put(refreshUser(token));

  if (BUILD_MODE === 'canada') {
    yield put(setFitToPlay(false));
  }

  // Set timeout block flag and show modal
  if (isTimeoutBlocked) {
    yield put(setTimeoutBlocked(true));
    yield put(modalOpen(MODALS.TIMEOUT_BLOCK));
  }

  yield put(loginSuccessDone());
}

// Logout user
export function* logoutSaga() {
  try {
    yield call(BffApiService.logout);
    triggerGTag(GTagEvents.logout_success);
  } catch (error) {
    // do nothing
  }

  yield put(logoutSuccess());
  useFluidPaymentsStore.getState().closeFluid();
  CookiesManager.removeTokenCookie();
  localStorage.removeItem('isTimeoutBlocked');
}

export function* logoutSuccessSaga() {
  yield put(autoLoginUpdate(AutoLoginStep.LoggedOut));
  yield put(clearUser());
  yield put(resetBonusesState());
  yield put(resetLimits());
  yield put(resetLoginHistory());
  yield put(resetUserPlayStatsAndLimits());
  yield put(kycReset());
  yield put(resetConsents());
  yield put(resetAml());
  yield put(setOntarioLocationCheckStatus(OntarioLocationCheckStatus.Idle));
  yield put(getBonuses());
}
